// src/component/page/airdrops/AirdropBubble.jsx
// Player Airdrops — frontend completo separado del casino.
// Conecta con su propio backend:
// GET  /api/player-airdrops/status
// GET  /api/player-airdrops/history
// GET  /api/player-airdrops/receipt/:airdropId
// POST /api/player-airdrops/create
// POST /api/player-airdrops/join
// POST /api/player-airdrops/cancel
// POST /api/player-airdrops/settle-expired

import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { keyframes } from "@mui/system";

/* ============================================================
   CONFIG
============================================================ */

const URL_MAIN =
  process.env.NODE_ENV === "production"
    ? "https://bigdogbone.up.railway.app"
    : "http://localhost:3002";

const AIRDROP_API = Object.freeze({
  status: `${URL_MAIN}/api/player-airdrops/status`,
  history: `${URL_MAIN}/api/player-airdrops/history`,
  receipt: (airdropId) =>
    `${URL_MAIN}/api/player-airdrops/receipt/${encodeURIComponent(airdropId)}`,
  create: `${URL_MAIN}/api/player-airdrops/create`,
  join: `${URL_MAIN}/api/player-airdrops/join`,
  cancel: `${URL_MAIN}/api/player-airdrops/cancel`,
  settleExpired: `${URL_MAIN}/api/player-airdrops/settle-expired`,
});

/* ============================================================
   HELPERS
============================================================ */

function getCookie(name) {
  if (typeof document === "undefined") return "";
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : "";
}

function getToken() {
  if (typeof window === "undefined") return "";
  return (
    getCookie("token") ||
    window.sessionStorage?.getItem("token") ||
    window.localStorage?.getItem("token") ||
    ""
  );
}

function authHeaders() {
  return { token: getToken() };
}

function requestId(prefix = "airdrop") {
  if (typeof window !== "undefined" && window.crypto?.randomUUID) {
    return `${prefix}-${window.crypto.randomUUID()}`;
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

function fmt(value, decimals = 3) {
  const number = Number(value || 0);
  if (!Number.isFinite(number)) return "0";

  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  }).format(number);
}

function shortId(value = "") {
  const text = String(value || "");
  if (text.length <= 16) return text;
  return `${text.slice(0, 8)}...${text.slice(-6)}`;
}

function messageFromError(error) {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    "The airdrop action could not be completed."
  );
}

function timeLeftLabel(value) {
  if (!value) return "—";

  const end = new Date(value).getTime();
  const diff = Math.max(0, end - Date.now());

  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days}d ${hours % 24}h`;
  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  if (minutes > 0) return `${minutes}m`;
  return `${Math.ceil(diff / 1000)}s`;
}

function durationHelper(ms) {
  const minutes = Math.round(Number(ms || 0) / 60000);
  if (!minutes) return "—";
  if (minutes < 60) return `${minutes} minutes`;

  const hours = minutes / 60;
  if (hours < 24) return `${Math.round(hours)} hours`;

  return `${Math.round(hours / 24)} days`;
}

function slotUsagePercentage(drop) {
  const max = Number(drop?.maxParticipants || 0);
  const count = Number(drop?.participantCount || 0);
  if (!max || max <= 0) return 0;
  return Math.max(0, Math.min(100, (count / max) * 100));
}

function statusInfo(status) {
  const value = String(status || "").toUpperCase();

  if (value === "COMPLETED") {
    return {
      label: "✅ PAID / SETTLED",
      shortLabel: "PAID",
      color: "#73ffbd",
      bg: "rgba(115,255,189,.12)",
      border: "rgba(115,255,189,.32)",
    };
  }

  if (value === "ACTIVE") {
    return {
      label: "⏳ ACTIVE",
      shortLabel: "ACTIVE",
      color: "#ffd45d",
      bg: "rgba(255,212,93,.12)",
      border: "rgba(255,212,93,.32)",
    };
  }

  if (value === "SETTLING") {
    return {
      label: "🔄 SETTLING",
      shortLabel: "SETTLING",
      color: "#79e8ff",
      bg: "rgba(121,232,255,.12)",
      border: "rgba(121,232,255,.32)",
    };
  }

  if (value === "CANCELLED") {
    return {
      label: "↩️ CANCELLED / REFUNDED",
      shortLabel: "REFUNDED",
      color: "#ffb86c",
      bg: "rgba(255,184,108,.12)",
      border: "rgba(255,184,108,.32)",
    };
  }

  if (value === "ERROR") {
    return {
      label: "⚠️ ERROR",
      shortLabel: "ERROR",
      color: "#ff8a8a",
      bg: "rgba(255,80,80,.12)",
      border: "rgba(255,80,80,.32)",
    };
  }

  return {
    label: value || "UNKNOWN",
    shortLabel: value || "UNKNOWN",
    color: "#ffffff",
    bg: "rgba(255,255,255,.08)",
    border: "rgba(255,255,255,.18)",
  };
}

/* ============================================================
   ANIMATIONS
============================================================ */

const neonPulse = keyframes`
  0%,100% { box-shadow: 0 0 14px rgba(255,183,0,.48), 0 0 35px rgba(126,55,255,.22); }
  50% { box-shadow: 0 0 28px rgba(255,183,0,.95), 0 0 66px rgba(126,55,255,.55); }
`;

const floatCard = keyframes`
  0%,100% { transform: translateY(0px); }
  50% { transform: translateY(-8px); }
`;

const shine = keyframes`
  0% { transform: translateX(-180%) skewX(-24deg); }
  60%,100% { transform: translateX(280%) skewX(-24deg); }
`;

const slotBounce = keyframes`
  0%,100% { transform: translateY(0) rotate(0deg); }
  35% { transform: translateY(-10px) rotate(-4deg); }
  70% { transform: translateY(3px) rotate(3deg); }
`;

const subtleGlow = keyframes`
  0%,100% { border-color: rgba(255,255,255,.12); }
  50% { border-color: rgba(255,221,100,.38); }
`;

/* ============================================================
   SMALL UI
============================================================ */

function GlassPanel({ children, sx = {} }) {
  return (
    <Paper
      elevation={0}
      sx={{
        color: "white",
        border: "1px solid rgba(255,255,255,.12)",
        background:
          "linear-gradient(145deg,rgba(255,255,255,.075),rgba(255,255,255,.02))",
        backdropFilter: "blur(14px)",
        borderRadius: "22px",
        animation: `${subtleGlow} 4s ease-in-out infinite`,
        ...sx,
      }}
    >
      {children}
    </Paper>
  );
}

function MetricBox({ label, value, accent = "#ffd45d", sub }) {
  return (
    <Box
      sx={{
        p: 1.1,
        borderRadius: "14px",
        border: "1px solid rgba(255,255,255,.08)",
        background: "rgba(255,255,255,.04)",
        minWidth: 0,
      }}
    >
      <Typography sx={{ fontSize: 10, color: "rgba(255,255,255,.5)" }}>
        {label}
      </Typography>
      <Typography
        sx={{
          mt: 0.2,
          fontWeight: 1000,
          color: accent,
          overflowWrap: "anywhere",
        }}
      >
        {value}
      </Typography>
      {sub ? (
        <Typography sx={{ fontSize: 10, color: "rgba(255,255,255,.42)" }}>
          {sub}
        </Typography>
      ) : null}
    </Box>
  );
}

function HelpLine({ children }) {
  return (
    <Typography
      sx={{
        position: "relative",
        pl: 2,
        mb: 0.65,
        color: "rgba(255,255,255,.72)",
        fontSize: 13,
        lineHeight: 1.65,
        "&::before": {
          content: '"•"',
          position: "absolute",
          left: 0,
          color: "#ffd45d",
          fontWeight: 1000,
        },
      }}
    >
      {children}
    </Typography>
  );
}

function HelpSection({ icon, title, accent = "#ffd45d", children }) {
  return (
    <GlassPanel
      sx={{
        p: { xs: 1.5, sm: 2 },
        height: "100%",
        borderColor: `${accent}55`,
        background: `linear-gradient(145deg,${accent}14,rgba(5,3,10,.9))`,
      }}
    >
      <Typography sx={{ mb: 1, color: accent, fontSize: 16, fontWeight: 1000 }}>
        {icon} {title}
      </Typography>
      {children}
    </GlassPanel>
  );
}

/* ============================================================
   HELP DIALOG
============================================================ */

function AirdropHelpDialog({ open, onClose }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          color: "white",
          borderRadius: "24px",
          border: "2px solid rgba(255,210,80,.85)",
          background:
            "radial-gradient(circle at top,rgba(255,170,0,.18),transparent 36%),linear-gradient(180deg,#170b20,#050309)",
          boxShadow: "0 0 55px rgba(255,190,40,.35)",
        },
      }}
    >
      <DialogTitle sx={{ pr: 7 }}>
        <Typography sx={{ color: "#ffd45d", fontSize: 24, fontWeight: 1000 }}>
          ❓ PLAYER AIRDROPS HELP
        </Typography>
        <Typography sx={{ color: "rgba(255,255,255,.6)", fontSize: 13 }}>
          Community drops created with the creator&apos;s own game resources.
        </Typography>

        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 12, top: 10, color: "white" }}
          aria-label="Close help"
        >
          ✕
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ borderColor: "rgba(255,255,255,.1)" }}>
        <Grid container spacing={1.5}>
          <Grid item xs={12} md={6}>
            <HelpSection icon="🎁" title="WHAT IS THIS?" accent="#ffd45d">
              <HelpLine>
                A real player creates the drop using resources already owned in
                Big Dog Bone or City Farm.
              </HelpLine>
              <HelpLine>
                The game does not create free rewards. It only locks and
                distributes what the creator deposits.
              </HelpLine>
              <HelpLine>
                All rewards are distributed when the countdown ends.
              </HelpLine>
            </HelpSection>
          </Grid>

          <Grid item xs={12} md={6}>
            <HelpSection icon="🔐" title="SECURITY RULES" accent="#79e8ff">
              <HelpLine>
                The selected resource is deducted from the creator when the drop
                is created.
              </HelpLine>
              <HelpLine>The creator cannot join their own drop.</HelpLine>
              <HelpLine>Each player can join only once per airdrop.</HelpLine>
              <HelpLine>
                Repeated clicks use a request ID to reduce duplicate actions.
              </HelpLine>
            </HelpSection>
          </Grid>

          <Grid item xs={12} md={6}>
            <HelpSection icon="⚖️" title="EQUAL SPLIT" accent="#73ffbd">
              <HelpLine>
                Final reward = total amount ÷ final number of participants.
              </HelpLine>
              <HelpLine>
                Example: 1,000 Water and 25 participants = 40 Water each.
              </HelpLine>
              <HelpLine>
                Players join first. Payment happens only when the timer ends.
              </HelpLine>
            </HelpSection>
          </Grid>

          <Grid item xs={12} md={6}>
            <HelpSection icon="🧾" title="RECEIPT / AUDIT" accent="#c18aff">
              <HelpLine>
                After a drop is settled, open the receipt to confirm if it paid.
              </HelpLine>
              <HelpLine>
                The receipt shows debit, payments, refunds, participants and
                balance before/after.
              </HelpLine>
              <HelpLine>
                If nobody joined, the full escrow returns to the creator.
              </HelpLine>
            </HelpSection>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
}

/* ============================================================
   ACTIVE CARD
============================================================ */

function AirdropActiveCard({ drop, busy, onJoin, onCancel }) {
  const joined = Boolean(drop.joinedByViewer);
  const active = drop.status === "ACTIVE";
  const isCreator = Boolean(drop.creator && drop.creator === drop.viewerName);
  const canJoin = Boolean(drop.canJoin) && !joined && !isCreator;
  const slotsPct = slotUsagePercentage(drop);

  return (
    <GlassPanel
      sx={{
        p: 1.35,
        borderColor: joined
          ? "rgba(115,255,189,.42)"
          : "rgba(255,211,78,.2)",
        background:
          "linear-gradient(145deg,rgba(255,210,80,.09),rgba(5,3,10,.88))",
      }}
    >
      <Stack direction="row" justifyContent="space-between" spacing={1}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 1000, color: "#ffd45d", fontSize: 15 }}>
            {drop.rewardIcon || "🎁"} {drop.creator} dropped{" "}
            {fmt(drop.totalAmount, drop.decimals)} {drop.rewardLabel}
          </Typography>

          {drop.message ? (
            <Typography
              sx={{
                mt: 0.2,
                color: "rgba(255,255,255,.62)",
                fontSize: 12,
                overflowWrap: "anywhere",
              }}
            >
              “{drop.message}”
            </Typography>
          ) : null}
        </Box>

        <Chip
          size="small"
          label={`⏳ ${timeLeftLabel(drop.expiresAt)}`}
          sx={{
            color: "#fff",
            fontWeight: 900,
            bgcolor: "rgba(255,255,255,.08)",
            flexShrink: 0,
          }}
        />
      </Stack>

      <Box sx={{ mt: 1 }}>
        <LinearProgress
          variant={drop.maxParticipants ? "determinate" : "indeterminate"}
          value={slotsPct || 0}
          sx={{
            height: 8,
            borderRadius: 999,
            bgcolor: "rgba(255,255,255,.06)",
            "& .MuiLinearProgress-bar": {
              borderRadius: 999,
              background: "linear-gradient(90deg,#ffd75b,#ff8c00,#ff57d2)",
            },
          }}
        />
      </Box>

      <Box
        sx={{
          mt: 1,
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr 1fr",
            sm: "repeat(4, minmax(0, 1fr))",
          },
          gap: 0.8,
        }}
      >
        <MetricBox
          label="PARTICIPANTS"
          value={`👥 ${fmt(drop.participantCount, 0)}${
            drop.maxParticipants ? ` / ${fmt(drop.maxParticipants, 0)}` : ""
          }`}
          accent="#ffffff"
        />
        <MetricBox
          label="CURRENT EACH"
          value={fmt(drop.estimatedPerParticipantCurrent, drop.decimals)}
          accent="#73ffbd"
        />
        <MetricBox
          label="IF YOU JOIN"
          value={fmt(drop.estimatedPerParticipantIfYouJoin, drop.decimals)}
          accent="#79e8ff"
        />
        <MetricBox label="TYPE" value={drop.rewardLabel} accent="#ffd45d" />
      </Box>

      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
        <Button
          fullWidth
          variant="contained"
          disabled={busy || !canJoin}
          onClick={() => onJoin(drop)}
          sx={{
            py: 1,
            color: "#120900",
            fontWeight: 1000,
            background: joined
              ? "linear-gradient(90deg,#73ffbd,#d9ff6f)"
              : "linear-gradient(90deg,#ffdf5b,#ff9d00)",
            "&:disabled": {
              color: "rgba(255,255,255,.55)",
              background: "rgba(255,255,255,.08)",
            },
          }}
        >
          {joined
            ? "✅ JOINED"
            : isCreator
              ? "YOUR DROP"
              : !active
                ? "ENDED"
                : drop.remainingSlots === 0
                  ? "FULL"
                  : "JOIN DROP"}
        </Button>

        {isCreator && drop.participantCount === 0 ? (
          <Tooltip title="Only the creator can cancel before anyone joins.">
            <span>
              <Button
                variant="outlined"
                disabled={busy}
                onClick={() => onCancel(drop)}
                sx={{
                  color: "#ff9a9a",
                  borderColor: "rgba(255,120,120,.35)",
                  fontWeight: 900,
                }}
              >
                CANCEL
              </Button>
            </span>
          </Tooltip>
        ) : null}
      </Stack>
    </GlassPanel>
  );
}

/* ============================================================
   HISTORY CARD
============================================================ */

function DropHistoryCard({ drop, onOpenReceipt }) {
  const info = statusInfo(drop.status);

  return (
    <GlassPanel
      sx={{
        p: 1.25,
        borderColor: info.border,
        background: `linear-gradient(145deg,${info.bg},rgba(5,3,10,.82))`,
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        justifyContent="space-between"
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 1000, color: "#ffd45d", fontSize: 14 }}>
            {drop.rewardIcon || "🎁"} {drop.creator} dropped{" "}
            {fmt(drop.totalAmount, drop.decimals)} {drop.rewardLabel}
          </Typography>

          <Typography sx={{ color: "rgba(255,255,255,.55)", fontSize: 11 }}>
            Created:{" "}
            {drop.createdAt ? new Date(drop.createdAt).toLocaleString() : "—"}
            {drop.settledAt
              ? ` · Settled: ${new Date(drop.settledAt).toLocaleString()}`
              : ""}
          </Typography>
        </Box>

        <Chip
          size="small"
          label={info.label}
          sx={{
            color: info.color,
            bgcolor: info.bg,
            border: `1px solid ${info.border}`,
            fontWeight: 1000,
            alignSelf: { xs: "flex-start", sm: "center" },
          }}
        />
      </Stack>

      <Box
        sx={{
          mt: 1,
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr 1fr",
            sm: "repeat(4, minmax(0, 1fr))",
          },
          gap: 0.75,
        }}
      >
        <MetricBox
          label="PARTICIPANTS"
          value={`👥 ${fmt(drop.participantCount, 0)}`}
          accent="#ffffff"
        />
        <MetricBox
          label="EACH PAID"
          value={`${fmt(drop.perParticipantAmount, drop.decimals)}`}
          accent="#73ffbd"
        />
        <MetricBox
          label="DISTRIBUTED"
          value={fmt(drop.distributedAmount, drop.decimals)}
          accent="#79e8ff"
        />
        <MetricBox
          label="REFUNDED"
          value={fmt(drop.refundedAmount, drop.decimals)}
          accent="#ffcf66"
        />
      </Box>

      <Button
        fullWidth
        variant="outlined"
        onClick={() => onOpenReceipt(drop.airdropId)}
        sx={{
          mt: 1,
          color: "#73ffbd",
          borderColor: "rgba(115,255,189,.32)",
          fontWeight: 1000,
        }}
      >
        🧾 VIEW RECEIPT / AUDIT
      </Button>
    </GlassPanel>
  );
}

/* ============================================================
   RECEIPT DIALOG
============================================================ */

function AirdropReceiptDialog({ open, receipt, loading, onClose }) {
  const drop = receipt?.airdrop || {};
  const summary = receipt?.summary || {};
  const participants = receipt?.participants || [];
  const ledger = receipt?.ledger || [];
  const info = statusInfo(summary.status || drop.status);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          color: "white",
          borderRadius: "24px",
          border: "2px solid rgba(115,255,189,.55)",
          background:
            "radial-gradient(circle at top,rgba(115,255,189,.14),transparent 38%),linear-gradient(180deg,#111021,#050309)",
          boxShadow: "0 0 55px rgba(115,255,189,.22)",
        },
      }}
    >
      <DialogTitle sx={{ pr: 7 }}>
        <Typography sx={{ color: "#73ffbd", fontSize: 24, fontWeight: 1000 }}>
          🧾 AIRDROP RECEIPT
        </Typography>
        <Typography sx={{ color: "rgba(255,255,255,.62)", fontSize: 13 }}>
          Full audit: debit, payment, refund and balance before/after.
        </Typography>

        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 12, top: 10, color: "white" }}
          aria-label="Close receipt"
        >
          ✕
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ borderColor: "rgba(255,255,255,.1)" }}>
        {loading ? (
          <Stack alignItems="center" spacing={1.5} sx={{ py: 7 }}>
            <CircularProgress sx={{ color: "#73ffbd" }} />
            <Typography sx={{ color: "rgba(255,255,255,.65)" }}>
              Loading receipt...
            </Typography>
          </Stack>
        ) : receipt ? (
          <Stack spacing={1.5}>
            <GlassPanel
              sx={{
                p: 1.5,
                borderColor: info.border,
                background: `linear-gradient(145deg,${info.bg},rgba(5,3,10,.86))`,
              }}
            >
              <Stack
                direction={{ xs: "column", md: "row" }}
                justifyContent="space-between"
                spacing={1.2}
              >
                <Box>
                  <Typography
                    sx={{ fontWeight: 1000, color: "#ffd45d", fontSize: 18 }}
                  >
                    {drop.rewardIcon || "🎁"} {drop.creator} dropped{" "}
                    {fmt(drop.totalAmount, drop.decimals)} {drop.rewardLabel}
                  </Typography>
                  <Typography sx={{ color: "rgba(255,255,255,.55)", fontSize: 12 }}>
                    Airdrop ID: {shortId(drop.airdropId)}
                  </Typography>
                </Box>

                <Chip
                  label={info.label}
                  sx={{
                    color: info.color,
                    bgcolor: info.bg,
                    border: `1px solid ${info.border}`,
                    fontWeight: 1000,
                    alignSelf: { xs: "flex-start", md: "center" },
                  }}
                />
              </Stack>
            </GlassPanel>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr 1fr",
                  md: "repeat(6, minmax(0, 1fr))",
                },
                gap: 1,
              }}
            >
              <MetricBox
                label="CREATOR DEBIT"
                value={fmt(summary.totalDebited, drop.decimals)}
                accent="#ffcf66"
              />
              <MetricBox
                label="PARTICIPANTS"
                value={`👥 ${fmt(summary.participants, 0)}`}
                accent="#ffffff"
              />
              <MetricBox
                label="PAID USERS"
                value={fmt(summary.paidParticipants, 0)}
                accent="#73ffbd"
              />
              <MetricBox
                label="EACH PAID"
                value={fmt(summary.perParticipantAmount, drop.decimals)}
                accent="#73ffbd"
              />
              <MetricBox
                label="DISTRIBUTED"
                value={fmt(summary.totalPaid || summary.distributedAmount, drop.decimals)}
                accent="#79e8ff"
              />
              <MetricBox
                label="REFUNDED"
                value={fmt(summary.totalRefunded || summary.refundedAmount, drop.decimals)}
                accent="#ffb86c"
              />
            </Box>

            <GlassPanel sx={{ p: 1.5 }}>
              <Typography sx={{ mb: 1, color: "#73ffbd", fontWeight: 1000 }}>
                👥 Participants paid
              </Typography>

              {participants.length ? (
                <Box sx={{ overflowX: "auto" }}>
                  <Box
                    component="table"
                    sx={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: 12,
                      "& th": {
                        textAlign: "left",
                        color: "rgba(255,255,255,.55)",
                        borderBottom: "1px solid rgba(255,255,255,.12)",
                        py: 0.8,
                      },
                      "& td": {
                        borderBottom: "1px solid rgba(255,255,255,.07)",
                        py: 0.8,
                        pr: 1,
                      },
                    }}
                  >
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>Status</th>
                        <th>Amount</th>
                        <th>Field</th>
                        <th>Before</th>
                        <th>After</th>
                        <th>Paid At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {participants.map((p) => (
                        <tr key={p.nombre}>
                          <td style={{ color: "#ffd45d", fontWeight: 900 }}>
                            {p.nombre}
                          </td>
                          <td>{p.status}</td>
                          <td style={{ color: "#73ffbd", fontWeight: 900 }}>
                            {fmt(p.amount, drop.decimals)}
                          </td>
                          <td>{p.field || "—"}</td>
                          <td>
                            {p.balanceBefore === null || p.balanceBefore === undefined
                              ? "—"
                              : fmt(p.balanceBefore, drop.decimals)}
                          </td>
                          <td>
                            {p.balanceAfter === null || p.balanceAfter === undefined
                              ? "—"
                              : fmt(p.balanceAfter, drop.decimals)}
                          </td>
                          <td>
                            {p.paidAt
                              ? new Date(p.paidAt).toLocaleString()
                              : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Box>
                </Box>
              ) : (
                <Typography sx={{ color: "rgba(255,255,255,.55)", fontSize: 13 }}>
                  No participants. If the airdrop ended, the full amount returns
                  to the creator.
                </Typography>
              )}
            </GlassPanel>

            <GlassPanel sx={{ p: 1.5 }}>
              <Typography sx={{ mb: 1, color: "#79e8ff", fontWeight: 1000 }}>
                📒 Balance movements
              </Typography>

              {ledger.length ? (
                <Stack spacing={0.7}>
                  {ledger.map((row, index) => (
                    <Box
                      key={`${row.type}-${row.nombre}-${index}`}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          md: "1.2fr .9fr .8fr .8fr .8fr 1.3fr",
                        },
                        gap: 0.8,
                        p: 0.9,
                        borderRadius: "12px",
                        border: "1px solid rgba(255,255,255,.08)",
                        bgcolor: "rgba(255,255,255,.035)",
                        fontSize: 12,
                      }}
                    >
                      <Box>
                        <b>{row.type}</b>
                        <Typography
                          sx={{ color: "rgba(255,255,255,.45)", fontSize: 10 }}
                        >
                          {row.createdAt
                            ? new Date(row.createdAt).toLocaleString()
                            : ""}
                        </Typography>
                      </Box>
                      <Box>{row.nombre}</Box>
                      <Box>{row.field || "—"}</Box>
                      <Box
                        sx={{
                          color: Number(row.amount) < 0 ? "#ff9a9a" : "#73ffbd",
                          fontWeight: 900,
                        }}
                      >
                        {Number(row.amount) > 0 ? "+" : ""}
                        {fmt(row.amount, drop.decimals)}
                      </Box>
                      <Box>
                        {row.balanceBefore === null || row.balanceBefore === undefined
                          ? "—"
                          : fmt(row.balanceBefore, drop.decimals)}
                      </Box>
                      <Box>
                        {row.balanceAfter === null || row.balanceAfter === undefined
                          ? "—"
                          : fmt(row.balanceAfter, drop.decimals)}
                      </Box>
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Typography sx={{ color: "rgba(255,255,255,.55)", fontSize: 13 }}>
                  No ledger movements found yet.
                </Typography>
              )}
            </GlassPanel>
          </Stack>
        ) : (
          <Typography sx={{ color: "rgba(255,255,255,.65)" }}>
            No receipt selected.
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ============================================================
   MAIN
============================================================ */

export default function AirdropBubble({ onNotice }) {
  const [open, setOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [receiptLoading, setReceiptLoading] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [internalNotice, setInternalNotice] = useState({
    open: false,
    type: "success",
    text: "",
  });

  const [rewardType, setRewardType] = useState("");
  const [totalAmount, setTotalAmount] = useState("");
  const [duration, setDuration] = useState("1h");
  const [maxParticipants, setMaxParticipants] = useState("");
  const [message, setMessage] = useState("");

  const notify = useCallback(
    (text, type = "success") => {
      if (typeof onNotice === "function") {
        onNotice(text, type);
        return;
      }

      setInternalNotice({
        open: true,
        type,
        text,
      });
    },
    [onNotice],
  );

  const loadAirdrops = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);

      try {
        const [statusResponse, historyResponse] = await Promise.all([
          axios.get(AIRDROP_API.status, {
            headers: authHeaders(),
          }),
          axios
            .get(`${AIRDROP_API.history}?limit=12`, {
              headers: authHeaders(),
            })
            .catch(() => ({ data: { history: [] } })),
        ]);

        const data = statusResponse.data || {};
        setStatus(data);
        setHistory(historyResponse.data?.history || []);

        const firstEnabled = (data.rewardTypes || []).find(
          (type) => !type.disabled,
        );
        setRewardType((current) => current || firstEnabled?.id || "");
      } catch (error) {
        if (!silent) notify(messageFromError(error), "error");
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [notify],
  );

  useEffect(() => {
    loadAirdrops(true);
    const timer = window.setInterval(() => loadAirdrops(true), 20000);
    return () => window.clearInterval(timer);
  }, [loadAirdrops]);

  useEffect(() => {
    if (open) loadAirdrops(true);
  }, [open, loadAirdrops]);

  const active = status?.active || [];
  const rewardTypes = (status?.rewardTypes || []).filter((type) => !type.disabled);
  const balances = status?.balances || {};
  const selectedType = rewardTypes.find((type) => type.id === rewardType);
  const selectedBalance = selectedType ? balances[selectedType.id] : null;
  const activeCount = active.length;

  const estimatedSplits = useMemo(() => {
    if (!selectedType) return null;

    const amount = Number(totalAmount || 0);
    const participants = Number(maxParticipants || 0);

    if (
      !Number.isFinite(amount) ||
      amount <= 0 ||
      !Number.isFinite(participants) ||
      participants <= 0
    ) {
      return null;
    }

    const decimals = Number(selectedType.decimals || 0);
    const each =
      Math.floor((amount / participants) * 10 ** decimals) / 10 ** decimals;
    const used = each * participants;
    const remainder = Math.max(0, amount - used);

    return { each, remainder };
  }, [selectedType, totalAmount, maxParticipants]);

  const createDrop = async () => {
    if (busy) return;

    const amount = Number(totalAmount);

    if (!rewardType) {
      notify("Choose what you want to drop.", "warning");
      return;
    }

    if (!Number.isFinite(amount) || amount <= 0) {
      notify("Enter a valid total amount.", "warning");
      return;
    }

    setBusy(true);

    try {
      const response = await axios.post(
        AIRDROP_API.create,
        {
          rewardType,
          totalAmount: amount,
          duration,
          maxParticipants: maxParticipants ? Number(maxParticipants) : 0,
          message,
          requestId: requestId("airdrop-create"),
        },
        { headers: authHeaders() },
      );

      setTotalAmount("");
      setMaxParticipants("");
      setMessage("");

      notify(
        `Airdrop created: ${fmt(
          response.data?.airdrop?.totalAmount,
          response.data?.airdrop?.decimals,
        )} ${response.data?.airdrop?.rewardLabel || ""}`,
        "success",
      );

      await loadAirdrops(true);
    } catch (error) {
      notify(messageFromError(error), "error");
    } finally {
      setBusy(false);
    }
  };

  const joinDrop = async (drop) => {
    if (busy || !drop?.airdropId) return;

    setBusy(true);

    try {
      await axios.post(
        AIRDROP_API.join,
        {
          airdropId: drop.airdropId,
          requestId: requestId("airdrop-join"),
        },
        { headers: authHeaders() },
      );

      notify(
        "You joined the airdrop. Rewards are paid when the timer ends.",
        "success",
      );

      await loadAirdrops(true);
    } catch (error) {
      notify(messageFromError(error), "error");
    } finally {
      setBusy(false);
    }
  };

  const cancelDrop = async (drop) => {
    if (busy || !drop?.airdropId) return;

    setBusy(true);

    try {
      await axios.post(
        AIRDROP_API.cancel,
        { airdropId: drop.airdropId },
        { headers: authHeaders() },
      );

      notify("Airdrop cancelled and escrow returned.", "info");
      await loadAirdrops(true);
    } catch (error) {
      notify(messageFromError(error), "error");
    } finally {
      setBusy(false);
    }
  };

  const loadReceipt = async (airdropId) => {
    if (!airdropId) return;

    setReceiptOpen(true);
    setReceiptLoading(true);

    try {
      const response = await axios.get(AIRDROP_API.receipt(airdropId), {
        headers: authHeaders(),
      });

      setSelectedReceipt(response.data);
    } catch (error) {
      notify(messageFromError(error), "error");
      setSelectedReceipt(null);
    } finally {
      setReceiptLoading(false);
    }
  };

  const settleExpired = async () => {
    if (busy) return;

    setBusy(true);

    try {
      const response = await axios.post(
        AIRDROP_API.settleExpired,
        { limit: 20 },
        { headers: authHeaders() },
      );

      const receipts = response.data?.receipts || [];

      notify(
        Number(response.data?.settled || 0) > 0
          ? `${response.data.settled} expired airdrop(s) settled. Receipt opened.`
          : "No expired airdrops to settle right now.",
        "info",
      );

      if (receipts.length) {
        setSelectedReceipt(receipts[0]);
        setReceiptOpen(true);
      }

      await loadAirdrops(true);
    } catch (error) {
      notify(messageFromError(error), "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          position: "fixed",
          right: { xs: 12, md: 22 },
          bottom: { xs: 82, md: 24 },
          zIndex: 1400,
        }}
      >
        <Button
          onClick={() => setOpen(true)}
          sx={{
            position: "relative",
            px: 1.6,
            py: 1.15,
            borderRadius: "18px",
            color: "#160900",
            fontWeight: 1000,
            border: "2px solid rgba(255,244,160,.95)",
            background:
              activeCount > 0
                ? "linear-gradient(90deg,#fff16b,#ff9d00,#ff6bd6)"
                : "linear-gradient(90deg,#ffe66d,#ffac1c)",
            boxShadow:
              activeCount > 0
                ? "0 0 20px rgba(255,190,40,.8),0 0 48px rgba(255,80,210,.34)"
                : "0 0 18px rgba(255,190,40,.45)",
            animation:
              activeCount > 0
                ? `${neonPulse} 1.8s ease-in-out infinite, ${slotBounce} 2.4s ease-in-out infinite`
                : `${floatCard} 4s ease-in-out infinite`,
            overflow: "hidden",
            "&::after": {
              content: '""',
              position: "absolute",
              top: "-50%",
              left: "-60%",
              width: "42%",
              height: "200%",
              background:
                "linear-gradient(90deg,transparent,rgba(255,255,255,.42),transparent)",
              animation: `${shine} 3s ease-in-out infinite`,
            },
          }}
        >
          <Stack spacing={0} alignItems="center" sx={{ lineHeight: 1 }}>
            <Typography sx={{ fontSize: 22, lineHeight: 1 }}>🎁</Typography>
            <Typography sx={{ fontSize: 12, fontWeight: 1000, lineHeight: 1 }}>
              AIRDROPS
            </Typography>
            <Typography sx={{ fontSize: 10, fontWeight: 1000, lineHeight: 1.2 }}>
              {activeCount} ACTIVE 🔥
            </Typography>
          </Stack>
        </Button>
      </Box>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="xl"
        PaperProps={{
          sx: {
            color: "white",
            borderRadius: { xs: "18px", sm: "28px" },
            border: "2px solid rgba(255,210,80,.9)",
            background:
              "radial-gradient(circle at top left,rgba(255,185,0,.22),transparent 34%),radial-gradient(circle at top right,rgba(255,70,214,.15),transparent 32%),linear-gradient(180deg,#170b22,#050309)",
            boxShadow:
              "0 0 35px rgba(255,190,40,.35),0 0 75px rgba(255,85,210,.2)",
            m: { xs: 1, sm: 2 },
            maxHeight: { xs: "96vh", sm: "94vh" },
          },
        }}
      >
        <DialogTitle sx={{ pr: 8 }}>
          <Typography
            sx={{
              color: "#ffd45d",
              fontSize: { xs: 23, sm: 31 },
              fontWeight: 1000,
            }}
          >
            🎁 PLAYER AIRDROPS
          </Typography>

          <Typography sx={{ color: "rgba(255,255,255,.62)", fontSize: 13 }}>
            Create community drops using your own BDB or City Farm resources.
            Rewards split equally when the timer ends.
          </Typography>

          <Stack
            direction="row"
            spacing={0.8}
            sx={{ position: "absolute", right: 12, top: 10 }}
          >
            <IconButton
              onClick={() => setHelpOpen(true)}
              sx={{ color: "#ffd45d", bgcolor: "rgba(255,255,255,.06)" }}
              aria-label="Airdrop help"
            >
              ?
            </IconButton>

            <IconButton
              onClick={() => setOpen(false)}
              sx={{ color: "white", bgcolor: "rgba(255,255,255,.06)" }}
              aria-label="Close airdrops"
            >
              ✕
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent dividers sx={{ borderColor: "rgba(255,255,255,.09)" }}>
          <Alert
            severity="info"
            sx={{
              mb: 1.5,
              color: "#dff8ff",
              bgcolor: "rgba(55,180,255,.09)",
              border: "1px solid rgba(90,210,255,.22)",
              "& .MuiAlert-icon": { color: "#6fe5ff" },
            }}
          >
            <b>How to know if it was paid:</b> when the timer ends, the drop
            moves from ACTIVE to HISTORY as <b>PAID / SETTLED</b>. Open the
            receipt to see who received rewards, balance before/after, and any
            refund.
          </Alert>

          <Grid container spacing={1.8}>
            <Grid item xs={12} lg={4.7}>
              <GlassPanel
                sx={{
                  p: 2,
                  borderColor: "rgba(255,211,78,.36)",
                  background:
                    "linear-gradient(145deg,rgba(255,180,30,.14),rgba(5,3,10,.92))",
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 1.1 }}
                >
                  <Box>
                    <Typography
                      sx={{ fontWeight: 1000, color: "#ffd45d", fontSize: 19 }}
                    >
                      + CREATE AIRDROP
                    </Typography>
                    <Typography sx={{ color: "rgba(255,255,255,.55)", fontSize: 12 }}>
                      Select a reward, review your balance, then create the drop.
                    </Typography>
                  </Box>

                  <Button
                    size="small"
                    onClick={() => loadAirdrops()}
                    disabled={loading}
                    sx={{ color: "#ffd45d", fontWeight: 900 }}
                  >
                    REFRESH
                  </Button>
                </Stack>

                <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,.58)", mb: 0.8 }}>
                  1) Choose reward type
                </Typography>

                <Stack
                  direction="row"
                  spacing={0.7}
                  useFlexGap
                  flexWrap="wrap"
                  sx={{ mb: 1.3, maxHeight: 160, overflowY: "auto", pr: 0.5 }}
                >
                  {rewardTypes.length ? (
                    rewardTypes.map((type) => {
                      const isActive = rewardType === type.id;
                      const typeBalance = balances?.[type.id];

                      return (
                        <Chip
                          key={type.id}
                          clickable
                          onClick={() => setRewardType(type.id)}
                          label={`${type.icon || "🎁"} ${type.label}`}
                          sx={{
                            color: isActive ? "#160900" : "white",
                            fontWeight: 950,
                            bgcolor: isActive ? "#ffd45d" : "rgba(255,255,255,.08)",
                            border: "1px solid rgba(255,255,255,.12)",
                            position: "relative",
                            "&::after": typeBalance?.available
                              ? {
                                  content: '""',
                                  position: "absolute",
                                  top: 4,
                                  right: 5,
                                  width: 6,
                                  height: 6,
                                  borderRadius: "50%",
                                  bgcolor: "#73ffbd",
                                }
                              : {},
                          }}
                        />
                      );
                    })
                  ) : (
                    <Typography sx={{ color: "rgba(255,255,255,.55)", fontSize: 13 }}>
                      No reward types available. Check backend connection.
                    </Typography>
                  )}
                </Stack>

                {selectedType ? (
                  <GlassPanel
                    sx={{
                      p: 1.2,
                      mb: 1.2,
                      borderColor: "rgba(110,224,255,.25)",
                      background:
                        "linear-gradient(145deg,rgba(53,169,255,.12),rgba(255,255,255,.02))",
                    }}
                  >
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      justifyContent="space-between"
                      spacing={1}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 1000, color: "#79e8ff" }}>
                          {selectedType.icon} {selectedType.label}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,.58)" }}>
                          Min {fmt(selectedType.minTotal, selectedType.decimals)} ·
                          Max {fmt(selectedType.maxTotal, selectedType.decimals)}
                          {selectedType.integerOnly ? " · Whole numbers only" : ""}
                        </Typography>
                      </Box>

                      <Box sx={{ textAlign: { xs: "left", sm: "right" } }}>
                        <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,.5)" }}>
                          AVAILABLE BALANCE
                        </Typography>
                        <Typography sx={{ fontWeight: 1000, color: "#73ffbd", fontSize: 18 }}>
                          {selectedBalance?.available
                            ? `${fmt(selectedBalance.balance, selectedType.decimals)} ${selectedType.unitLabel}`
                            : `0 ${selectedType.unitLabel}`}
                        </Typography>
                        <Typography sx={{ fontSize: 10, color: "rgba(255,255,255,.38)" }}>
                          {selectedBalance?.field
                            ? `Field: ${selectedBalance.field}`
                            : "Resource not available in this account"}
                        </Typography>
                      </Box>
                    </Stack>
                  </GlassPanel>
                ) : null}

                <Grid container spacing={1}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Total amount"
                      value={totalAmount}
                      onChange={(event) => setTotalAmount(event.target.value)}
                      disabled={busy}
                      helperText="Total locked from your account"
                      FormHelperTextProps={{ sx: { color: "rgba(255,255,255,.45)" } }}
                      sx={{
                        input: { color: "white", fontWeight: 900 },
                        label: { color: "rgba(255,255,255,.65)" },
                        "& .MuiOutlinedInput-root fieldset": {
                          borderColor: "rgba(255,255,255,.25)",
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Duration"
                      value={duration}
                      onChange={(event) => setDuration(event.target.value)}
                      disabled={busy}
                      helperText="Examples: 10m, 1h, 1d"
                      FormHelperTextProps={{ sx: { color: "rgba(255,255,255,.45)" } }}
                      sx={{
                        input: { color: "white", fontWeight: 900 },
                        label: { color: "rgba(255,255,255,.65)" },
                        "& .MuiOutlinedInput-root fieldset": {
                          borderColor: "rgba(255,255,255,.25)",
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Max participants (optional)"
                      value={maxParticipants}
                      onChange={(event) => setMaxParticipants(event.target.value)}
                      disabled={busy}
                      helperText="Whole items are protected automatically"
                      FormHelperTextProps={{ sx: { color: "rgba(255,255,255,.45)" } }}
                      sx={{
                        input: { color: "white", fontWeight: 900 },
                        label: { color: "rgba(255,255,255,.65)" },
                        "& .MuiOutlinedInput-root fieldset": {
                          borderColor: "rgba(255,255,255,.25)",
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Message"
                      value={message}
                      onChange={(event) => setMessage(event.target.value)}
                      disabled={busy}
                      inputProps={{ maxLength: 140 }}
                      helperText="Optional message"
                      FormHelperTextProps={{ sx: { color: "rgba(255,255,255,.45)" } }}
                      sx={{
                        input: { color: "white", fontWeight: 900 },
                        label: { color: "rgba(255,255,255,.65)" },
                        "& .MuiOutlinedInput-root fieldset": {
                          borderColor: "rgba(255,255,255,.25)",
                        },
                      }}
                    />
                  </Grid>
                </Grid>

                {estimatedSplits ? (
                  <Box
                    sx={{
                      mt: 1.2,
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(3, minmax(0, 1fr))",
                      },
                      gap: 0.8,
                    }}
                  >
                    <MetricBox
                      label="EST. EACH"
                      value={`${fmt(estimatedSplits.each, selectedType?.decimals || 0)} ${
                        selectedType?.unitLabel || ""
                      }`}
                      accent="#73ffbd"
                    />
                    <MetricBox
                      label="REMAINDER"
                      value={`${fmt(estimatedSplits.remainder, selectedType?.decimals || 0)} ${
                        selectedType?.unitLabel || ""
                      }`}
                      accent="#ffcf66"
                    />
                    <MetricBox
                      label="SOURCE"
                      value={selectedType?.source === "city" ? "CITY FARM" : "BIG DOG BONE"}
                      accent="#79e8ff"
                    />
                  </Box>
                ) : null}

                <Button
                  fullWidth
                  variant="contained"
                  onClick={createDrop}
                  disabled={busy || !rewardType || !rewardTypes.length}
                  sx={{
                    mt: 1.5,
                    py: 1.25,
                    color: "#160900",
                    fontWeight: 1000,
                    background: "linear-gradient(90deg,#fff071,#ff9b00)",
                  }}
                >
                  {busy ? "PROCESSING..." : "CREATE AIRDROP"}
                </Button>

                <Divider sx={{ my: 1.5, borderColor: "rgba(255,255,255,.08)" }} />

                <Typography sx={{ color: "rgba(255,255,255,.55)", fontSize: 12, lineHeight: 1.6 }}>
                  Min duration: {durationHelper(status?.config?.minDurationMs)} ·
                  Max duration: {durationHelper(status?.config?.maxDurationMs)}
                </Typography>
              </GlassPanel>
            </Grid>

            <Grid item xs={12} lg={7.3}>
              <GlassPanel sx={{ p: 2, minHeight: 430 }}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  spacing={1}
                  sx={{ mb: 1.2 }}
                >
                  <Box>
                    <Typography sx={{ fontWeight: 1000, color: "#73ffbd", fontSize: 19 }}>
                      🔥 ACTIVE NOW
                    </Typography>
                    <Typography sx={{ color: "rgba(255,255,255,.55)", fontSize: 12 }}>
                      Join now. Payment is made automatically when the timer ends.
                    </Typography>
                  </Box>

                  <Stack direction="row" spacing={0.8}>
                    <Chip
                      label={`${activeCount} ACTIVE`}
                      sx={{
                        color: "#00170d",
                        bgcolor: "#73ffbd",
                        fontWeight: 1000,
                      }}
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={settleExpired}
                      disabled={busy}
                      sx={{
                        color: "#ffd45d",
                        borderColor: "rgba(255,211,78,.45)",
                        fontWeight: 900,
                      }}
                    >
                      SETTLE
                    </Button>
                  </Stack>
                </Stack>

                {loading ? (
                  <Stack alignItems="center" spacing={1.5} sx={{ py: 7 }}>
                    <CircularProgress sx={{ color: "#ffd45d" }} />
                    <Typography sx={{ color: "rgba(255,255,255,.65)" }}>
                      Loading airdrops...
                    </Typography>
                  </Stack>
                ) : active.length ? (
                  <Stack spacing={1}>
                    {active.map((drop) => (
                      <AirdropActiveCard
                        key={drop.airdropId}
                        drop={{
                          ...drop,
                          viewerName: status?.user?.nombre || "",
                        }}
                        busy={busy}
                        onJoin={joinDrop}
                        onCancel={cancelDrop}
                      />
                    ))}
                  </Stack>
                ) : (
                  <Box
                    sx={{
                      py: 8,
                      textAlign: "center",
                      border: "1px dashed rgba(255,255,255,.16)",
                      borderRadius: "18px",
                      bgcolor: "rgba(255,255,255,.035)",
                    }}
                  >
                    <Typography sx={{ fontSize: 42 }}>🎁</Typography>
                    <Typography sx={{ fontWeight: 1000, color: "#ffd45d" }}>
                      No active airdrops right now
                    </Typography>
                    <Typography sx={{ color: "rgba(255,255,255,.55)", fontSize: 13 }}>
                      Be the first to create one for the community.
                    </Typography>
                  </Box>
                )}
              </GlassPanel>
            </Grid>

            <Grid item xs={12}>
              <GlassPanel
                sx={{
                  p: 2,
                  borderColor: "rgba(115,255,189,.22)",
                  background:
                    "linear-gradient(145deg,rgba(115,255,189,.07),rgba(5,3,10,.88))",
                }}
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  spacing={1}
                  sx={{ mb: 1.2 }}
                >
                  <Box>
                    <Typography sx={{ fontWeight: 1000, color: "#73ffbd", fontSize: 18 }}>
                      ✅ PAYMENT HISTORY
                    </Typography>
                    <Typography sx={{ color: "rgba(255,255,255,.55)", fontSize: 12 }}>
                      Completed, cancelled and refunded drops appear here.
                    </Typography>
                  </Box>

                  <Chip
                    label={`${history.length} RECENT`}
                    sx={{
                      color: "#00170d",
                      bgcolor: "#73ffbd",
                      fontWeight: 1000,
                      alignSelf: { xs: "flex-start", sm: "center" },
                    }}
                  />
                </Stack>

                {history.length ? (
                  <Grid container spacing={1}>
                    {history.map((drop) => (
                      <Grid key={drop.airdropId} item xs={12} md={6}>
                        <DropHistoryCard drop={drop} onOpenReceipt={loadReceipt} />
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Box
                    sx={{
                      py: 3.5,
                      textAlign: "center",
                      border: "1px dashed rgba(255,255,255,.14)",
                      borderRadius: "18px",
                      bgcolor: "rgba(255,255,255,.03)",
                    }}
                  >
                    <Typography sx={{ fontSize: 32 }}>📜</Typography>
                    <Typography sx={{ fontWeight: 1000, color: "#ffd45d" }}>
                      No payment history yet
                    </Typography>
                    <Typography sx={{ color: "rgba(255,255,255,.55)", fontSize: 13 }}>
                      When an airdrop expires and is settled, it will show here.
                    </Typography>
                  </Box>
                )}
              </GlassPanel>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>

      <AirdropHelpDialog open={helpOpen} onClose={() => setHelpOpen(false)} />

      <AirdropReceiptDialog
        open={receiptOpen}
        receipt={selectedReceipt}
        loading={receiptLoading}
        onClose={() => setReceiptOpen(false)}
      />

      <Snackbar
        open={internalNotice.open}
        autoHideDuration={4200}
        onClose={() =>
          setInternalNotice((current) => ({
            ...current,
            open: false,
          }))
        }
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={internalNotice.type}
          onClose={() =>
            setInternalNotice((current) => ({
              ...current,
              open: false,
            }))
          }
          sx={{ fontWeight: 850 }}
        >
          {internalNotice.text}
        </Alert>
      </Snackbar>
    </>
  );
}
